// theme/theme.js
import { createTheme } from "@mui/material/styles";

/**
 * Creates an app theme with direction and mode support
 * @param {string} direction - Text direction ('ltr' or 'rtl')
 * @param {string} mode - Theme mode ('light' or 'dark')
 * @returns {Object} - Material-UI theme object
 */
const createAppTheme = (direction = 'ltr', mode = 'light') => {
  // ✅ First, define your base colors

  //const primaryMain = mode === 'dark' ? "#1d8354" : "#07390fff";
  const primaryMain = mode === 'dark' ? "#07390fff" : "#1d8354";
  const secondaryMain = "#1d8354";
  const contrastMain = "#ffffff";
  /* const primaryMain = mode === 'dark' ? "#07390fff" : "#fff";
  const secondaryMain = mode === 'dark' ? "#1d8354" : "#1d8354";
  const contrastMain = mode === 'dark' ? "#fff" : "#1d8354"; */

  return createTheme({
    direction: direction,
    palette: {
      mode: mode,
      primary: {
        main: primaryMain,
        dark: mode === 'dark' ? "#1d7a45" : "#155d3a",
        light: mode === 'dark' ? "#1d8354" : "#0b3320ff",
        contrastText: contrastMain,
      },
      secondary: {
        main: secondaryMain,//mode === 'dark' ? "#5e94faff" : "#3e7ddbfa",
      },
      success: {
        main: mode === 'dark' ? "#07390fff" : "#1d8354",
        light: "#66bb6a"
      },
      warning: {
        main: "#ed6c02",
        light: "#ff9800"
      },
      error: {
        main: "#d32f2f",
        light: "#f44336"
      },
      ...(mode === 'dark' && {
        background: {
          default: "#0a0e27",
          paper: "#151a2e",
        },
        text: {
          primary: "#031e62ff",
          secondary: "#b0b3b8",
        },
        divider: "rgba(255, 255, 255, 0.12)",
      }),
      ...(mode === 'light' && {
        background: {
          default: "#fafafa",
          paper: "#ffffff",
        },
        text: {
          primary: "#1a1a1a",
          secondary: "#666666",
        },
        divider: "rgba(0, 0, 0, 0.12)",
      }),
    },
    typography: {
      fontFamily: direction === 'rtl'
        ? "Cairo, Tajawal, Arial, sans-serif"
        : "Roboto, Arial, sans-serif",
      // ✅ Set colors for specific variants
      /* h6: {
        color: secondaryMain
      },
      body2: {
        color: secondaryMain
      },
      // Or set for all variants
      allVariants: {
        color: secondaryMain
      } */
      allVariants: {
        color: 'inherit', // This will inherit from text.primary by default
      }
    },

    // ✅ Component overrides for consistent styling
    components: {
      MuiTableContainer: {
        styleOverrides: {
          root: {
            backgroundColor: mode === 'dark' ? '#151a2e' : '#ffffff',
          },
        },
      },
      MuiTable: {
        styleOverrides: {
          root: {
            borderCollapse: 'separate',
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            // Header row styling
            '& .MuiTableRow-root': {
              backgroundColor: mode === 'dark'
                ? 'rgba(38, 166, 91, 0.15)' // Dark mode: translucent primary
                : 'rgba(29, 131, 84, 0.1)',  // Light mode: translucent primary
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: mode === 'dark'
              ? '1px solid rgba(255, 255, 255, 0.12)'
              : '1px solid rgba(0, 0, 0, 0.12)',
            color: mode === 'dark' ? '#e4e6eb' : '#1a1a1a',
            textAlign: direction === 'rtl' ? 'right' : 'left'
          },
          head: {
            // ✅ Table header cells
            fontWeight: 700,
            backgroundColor: primaryMain,
            color: '#ffffff',
            fontSize: '0.875rem',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            // Make sticky headers work properly
            position: 'sticky',
            textAlign: direction === 'rtl' ? 'right' : 'left',
            top: 0,
            zIndex: 10,
          },
          body: {
            // ✅ Table body cells
            fontSize: '0.875rem',
            /* backgroundColor: mode === 'light'
              ? '#7a1d5eff'
              : '#64831dff', */
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            // ✅ Zebra striping
            '&:nth-of-type(odd)': {
              backgroundColor: mode === 'dark'
                ? 'rgba(255, 255, 255, 0.05)'
                : 'rgba(0, 0, 0, 0.02)',
            },
            '&:nth-of-type(even)': {
              backgroundColor: mode === 'dark'
                ? '#151a2e' // background.paper
                : '#ffffff',
            },
            // ✅ Hover effect
            '&:hover': {
              backgroundColor: mode === 'dark'
                ? 'rgba(255, 255, 255, 0.1)'
                : 'rgba(0, 0, 0, 0.04)',
              transition: 'background-color 0.2s ease',
            },
            // ✅ Clickable rows
            '&.MuiTableRow-hover': {
              cursor: 'pointer',
            },
          },
        },
      },
      MuiTableSortLabel: {
        styleOverrides: {
          root: {
            color: '#ffffff',
            '&:hover': {
              color: '#ffffff',
            },
            '&.Mui-active': {
              color: '#ffffff',
              '& .MuiTableSortLabel-icon': {
                color: '#ffffff',
              },
            },
          },
          icon: {
            color: '#ffffff !important',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none', // Remove default MUI gradient
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none', // More modern look
          },
          startIcon: {
            marginRight: direction === 'rtl' ? 0 : 8,
            marginLeft: direction === 'rtl' ? 8 : 0,
          },
          endIcon: {
            marginLeft: direction === 'rtl' ? 0 : 8,
            marginRight: direction === 'rtl' ? 8 : 0,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          icon: {
            marginLeft: direction === 'rtl' ? '-6px' : '5px',
            marginRight: direction === 'rtl' ? '5px' : '-6px',
          },
        },
      },


    },

    // Your existing custom loading backdrop configurations
    loadingBackdrop: {
      colors: {
        primary: {
          main: "#1976d2",
          light: "#42a5f5",
          text: "#fff"
        },
        secondary: {
          main: "#dc004e",
          light: "#e91e63",
          text: "#fff"
        },
        success: {
          main: "#2e7d32",
          light: "#66bb6a",
          text: "#fff"
        },
        warning: {
          main: "#ed6c02",
          light: "#ff9800",
          text: "#fff"
        },
        error: {
          main: "#d32f2f",
          light: "#f44336",
          text: "#fff"
        },
        white: {
          main: "#fff",
          light: "#f5f5f5",
          text: "#fff"
        },
        gradient: {
          main: "linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%)",
          light: "#FF8E53",
          text: "#fff"
        },
        greenGradient: {
          main: "linear-gradient(45deg, #1d8354 30%, #66bb6a 90%)",
          light: "#66bb6a",
          text: "#fff"
        },
        blueGradient: {
          main: "linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)",
          light: "#42a5f5",
          text: "#fff"
        },
        purpleGradient: {
          main: "linear-gradient(45deg, #7b1fa2 30%, #ba68c8 90%)",
          light: "#ba68c8",
          text: "#fff"
        },
        orangeGradient: {
          main: "linear-gradient(45deg, #f57c00 30%, #ffb74d 90%)",
          light: "#ffb74d",
          text: "#fff"
        },
        emeraldGradient: {
          main: "linear-gradient(45deg, #047857 20%, #10b981 60%, #6ee7b7 100%)",
          light: "#6ee7b7",
          text: "#fff"
        },
        tealGradient: {
          main: "linear-gradient(45deg, #0d9488 30%, #5eead4 90%)",
          light: "#5eead4",
          text: "#fff"
        }
      },
      defaultVariant: "spinner",
      defaultColor: "primary"
    }
  });
}


export default createAppTheme;